import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  HelpCircle, 
  Book, 
  MessageCircle, 
  ExternalLink, 
  Search, 
  ChevronDown, 
  ChevronRight,
  Phone,
  Mail,
  Globe
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "How do I create a new queue?",
    answer: "Go to Settings > Queues tab and click 'Create Queue'. Configure location, service type, party size settings, and branding options. The queue will be immediately available for customers to join via SMS or web.",
    category: "Queue Management"
  },
  {
    question: "How do customers join a queue?",
    answer: "Customers have three ways to join: (1) Text a keyword (e.g., 'JOIN') directly to your SMS number for instant queue entry. (2) Visit your custom queue URL and fill out the web form. (3) Use natural language with AI-enabled queues (e.g., 'I want to JOIN'). All methods send an SMS confirmation with position and estimated wait time.",
    category: "Customer Experience"
  },
  {
    question: "How do I set up keyword-to-join for my queue?",
    answer: "Go to Settings > Queues, select your queue, and enter a Join Keyword (e.g., 'JOIN', 'CHECKIN', 'REGISTER'). The keyword must be unique per SMS number and can be up to 20 characters. Customers can then text just that keyword to instantly join your queue. Works with both AI and non-AI tenants.",
    category: "Queue Management"
  },
  {
    question: "Can I customize SMS messages?",
    answer: "Yes! Go to Messaging > SMS Templates to customize all 16 automated message templates across 5 categories. Use variables like {position}, {expected_wait}, {location_label}, and {tenant} to personalize messages.",
    category: "SMS & Messaging"
  },
  {
    question: "How do I handle no-shows?",
    answer: "When a customer doesn't respond to their 'now serving' message, use the 'Mark No-Show' button. The system automatically requeues them using EventBridge Scheduler based on your configured requeue settings.",
    category: "Queue Management"
  },
  {
    question: "How are wait times calculated?",
    answer: "Wait times use a smart hybrid approach: (1) When you have 5+ completed services in the last 30 days, we use the 80th percentile (P80) of actual service times for statistical accuracy. (2) For new queues, we use your configured 'Estimated Service Time' setting. (3) The formula is: (your position - 1) × average service time. Configure estimated service time in Settings > Queues (1-120 minutes, default 10 minutes).",
    category: "AI & Wait Times"
  },
  {
    question: "What reports are available?",
    answer: "The Reports tab provides comprehensive analytics: Overview (KPIs), Wait Times (P80 predictions), Throughput (service efficiency), Customer Search, and SMS Analytics. All reports support date filtering and CSV export. Dashboard shows real-time metrics with 60-80% faster loading via caching.",
    category: "Analytics & Reports"
  },
  {
    question: "How do I manage users and permissions?",
    answer: "In the Users tab, admins can view, add, and manage team members. Assign roles (Super Admin, Tenant Admin, Manager, Viewer) with specific permissions. Use the 'Impersonate User' feature for support scenarios. Password reset and role changes are available.",
    category: "User Management"
  },
  {
    question: "What AI features are available?",
    answer: "AI features vary by plan: (1) Premium: Shared Lex bot (TCHLAQX9JH) for natural language SMS understanding. (2) Enterprise: Custom per-tenant Lex bots with business-specific intents and vocabulary. AI handles queries like 'How long is the wait?' and 'Push me back 10 minutes' naturally.",
    category: "AI & Wait Times"
  },
  {
    question: "How do SMS credits work?",
    answer: "SMS credits are managed via the SMS Ledger system: (1) Monthly allowances are automatically deposited based on your plan. (2) Usage is tracked per customer with 'included' vs 'overage' labeling. (3) Overage is automatically reported to Stripe every 30 minutes for billing. (4) Purchase additional SMS bundles in the Billing tab. View your balance on the Dashboard.",
    category: "Billing & SMS"
  },
  {
    question: "How do I configure party sizes?",
    answer: "In Settings > Queues, enable 'Party Size Collection' for each queue and set the maximum party size (1-20). Plan limits apply: Professional/Trial (2 queues), Premium (10 queues), Enterprise (20 queues). Customers can specify party size when joining via web or SMS.",
    category: "Queue Management"
  },
  {
    question: "How do I start a 14-day free trial?",
    answer: "Visit the trial signup page, enter your business email, and verify via the email link. A new tenant account is automatically created with trial plan access. Upgrade to a paid plan anytime in the Billing tab. Trial status is shown on the Dashboard.",
    category: "Billing & Plans"
  },
  {
    question: "How do I manage my subscription?",
    answer: "In the Billing tab, view your current plan, change plans (Professional $59, Premium $89, Enterprise $149), manage add-ons like dedicated phone numbers ($10/month), and view billing history. Plan changes take effect immediately.",
    category: "Billing & Plans"
  },
  {
    question: "What's the difference between plan tiers?",
    answer: "Professional ($59): Core queue management, 2 queues, basic SMS. Premium ($89): AI features, 10 queues, shared Lex bot, advanced analytics. Enterprise ($149): Custom Lex bots, 20 queues, white labeling, priority support. All plans include unlimited users and locations.",
    category: "Billing & Plans"
  },
  {
    question: "How are journeys and customers counted in reports?",
    answer: "QueueMeIn distinguishes between journeys and unique customers: (1) A Journey is a complete queue experience from join to completion, including any requeues. (2) A Unique Customer is a distinct phone number across all journeys. (3) Requeues continue the same journey rather than starting new ones, ensuring accurate customer experience reporting.",
    category: "Analytics & Reports"
  }
];

export function Help() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });

  const filteredFAQs = FAQ_ITEMS.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(FAQ_ITEMS.map(item => item.category))];

  const handleSupportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Support request submitted:', supportForm);
    // Reset form
    setSupportForm({ subject: '', message: '', priority: 'medium' });
    alert('Support request submitted! We\'ll get back to you within 24 hours.');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Help & Support</h1>
        <p className="text-muted-foreground">
          Find answers, documentation, and get support for QueueMeIn
        </p>
      </div>

      <Tabs defaultValue="faq" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="docs" className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            Documentation
          </TabsTrigger>
          <TabsTrigger value="support" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Contact Support
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            Resources
          </TabsTrigger>
        </TabsList>

        <TabsContent value="faq" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardDescription>Find quick answers to common questions</CardDescription>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {categories.map(category => {
                const categoryFAQs = filteredFAQs.filter(item => item.category === category);
                if (categoryFAQs.length === 0) return null;

                return (
                  <div key={category} className="space-y-2">
                    <h3 className="font-semibold text-lg text-blue-600">{category}</h3>
                    {categoryFAQs.map((faq) => {
                      const globalIndex = FAQ_ITEMS.indexOf(faq);
                      const isExpanded = expandedFAQ === globalIndex;
                      
                      return (
                        <div key={globalIndex} className="border rounded-lg">
                          <button
                            onClick={() => setExpandedFAQ(isExpanded ? null : globalIndex)}
                            className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
                          >
                            <span className="font-medium">{faq.question}</span>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="px-4 pb-4 text-gray-600">
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="docs" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Getting Started</CardTitle>
                <CardDescription>Essential guides for new users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Quick Start Guide
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  14-Day Free Trial Setup
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Setting Up Your First Queue
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  SMS Templates & Messaging
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  User Roles & Permissions
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Advanced Features</CardTitle>
                <CardDescription>In-depth feature documentation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open('/docs.html', '_blank')}
                >
                  <Book className="h-4 w-4 mr-2" />
                  API Documentation
                  <ExternalLink className="h-4 w-4 ml-auto" />
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  QueueAI & Custom Lex Bots
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  SMS Credit System & Billing
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Party Size Configuration
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Keyword-to-Join Queue Setup
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Subscription Management
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Analytics & P80 Wait Time Predictions
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Multi-Tenant Architecture
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Troubleshooting</CardTitle>
                <CardDescription>Common issues and solutions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  SMS Delivery & Carrier Issues
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  AI Bot Not Responding
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Billing & Credit Issues
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Queue Configuration Problems
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Login & Permission Issues
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Video Tutorials</CardTitle>
                <CardDescription>Step-by-step video guides</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Platform Overview (5 min)
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Queue Setup Tutorial (8 min)
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  SMS Templates Guide (6 min)
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Reports Walkthrough (10 min)
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="support" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>Get in touch with our support team</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 border rounded">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium">Email Support</div>
                    <div className="text-sm text-muted-foreground">support@queuemein.com</div>
                    <div className="text-xs text-muted-foreground">Response within 24 hours</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 border rounded">
                  <Phone className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="font-medium">Phone Support</div>
                    <div className="text-sm text-muted-foreground">+1 (555) 123-4567</div>
                    <div className="text-xs text-muted-foreground">Mon-Fri 9AM-6PM EST</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded">
                  <Globe className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="font-medium">Live Chat</div>
                    <div className="text-sm text-muted-foreground">Available on website</div>
                    <div className="text-xs text-muted-foreground">Mon-Fri 9AM-6PM EST</div>
                  </div>
                </div>

                <div className="pt-4">
                  <h4 className="font-medium mb-2">Support Hours</h4>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>Monday - Friday</span>
                      <span>9:00 AM - 6:00 PM EST</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday</span>
                      <span>10:00 AM - 2:00 PM EST</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday</span>
                      <span>Closed</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Submit Support Request</CardTitle>
                <CardDescription>Send us a detailed message about your issue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSupportSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Subject</label>
                    <Input
                      value={supportForm.subject}
                      onChange={(e) => setSupportForm(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Brief description of your issue"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Priority</label>
                    <select 
                      className="w-full p-2 border rounded-md"
                      value={supportForm.priority}
                      onChange={(e) => setSupportForm(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      <option value="low">Low - General question</option>
                      <option value="medium">Medium - Feature request</option>
                      <option value="high">High - System issue</option>
                      <option value="urgent">Urgent - Service down</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Message</label>
                    <Textarea
                      value={supportForm.message}
                      onChange={(e) => setSupportForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Please provide detailed information about your issue, including steps to reproduce if applicable..."
                      className="min-h-32"
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    Submit Support Request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="resources" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>System Status</CardTitle>
                <CardDescription>Check current system health</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">API Status</span>
                  <Badge className="bg-green-500">Operational</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">SMS Service</span>
                  <Badge className="bg-green-500">Operational</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Web Interface</span>
                  <Badge className="bg-green-500">Operational</Badge>
                </div>
                <Button variant="outline" className="w-full mt-3">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Status Page
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Community</CardTitle>
                <CardDescription>Connect with other users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  User Forum
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Feature Requests
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Best Practices
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Success Stories
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Updates & News</CardTitle>
                <CardDescription>Stay informed about new features</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Release Notes
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Product Roadmap
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Newsletter Signup
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Blog
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}